import { HomePosts } from 'types/home';
import { PostCardInfo } from 'types/post';

/**
 * True when the homepage payload has a hero, a featured slot, or a latest post.
 * An empty `getStaticProps` fallback has settings only, so this stays false.
 */
/**
 * The hero is rendered only after a live `homepageSettings` payload says it is on.
 * Static props and the failed-fetch fallback must not paint it.
 */
export function shouldRenderHero(
  settings: HomePosts['homepageSettings'] | null | undefined,
): boolean {
  return settings?.showHero === true;
}

export function hasDisplayableHomePosts(posts: HomePosts | null | undefined): boolean {
  if (!posts) return false;
  const featured = posts.featuredPosts;
  if (featured?._0 || featured?._1 || featured?._2 || featured?._3 || featured?._4) {
    return true;
  }
  return (posts.publishedPosts?.length ?? 0) > 0;
}

/**
 * Skeletons stay up until a query result includes posts.
 * A saved empty fallback must not count as loaded content.
 */
export function isHomeFeedLoading(options: {
  posts: HomePosts | null | undefined;
  loading: boolean;
  hasQueryResult: boolean;
  error?: unknown;
}): boolean {
  if (options.error || hasDisplayableHomePosts(options.posts)) return false;
  return options.loading || !options.hasQueryResult;
}

/**
 * Empty post card used as placeholder when featured posts are not available.
 * This prevents rendering errors and provides a consistent fallback.
 */
export const EMPTY_POST_CARD: PostCardInfo = {
  title: '',
  image: {
    mediaId: 'e647bc92-ee7c-44aa-9ed3-ec5e1791627d',
    path: '2019/10/26fa0941-1a48-4cc3-ba2f-4f550c868ff6.jpeg',
    sourceUrl:
      'https://elevator-media.imgix.net/2019/10/26fa0941-1a48-4cc3-ba2f-4f550c868ff6.jpeg?w=400&h=300&ixlib=js-2.1.2&s=b43ca36e9ae014eefa63c9fafa0c8d5d',
    sourceSet:
      'https://elevator-media.imgix.net/2019/10/26fa0941-1a48-4cc3-ba2f-4f550c868ff6.jpeg?w=100&ixlib=js-2.1.2&s=d11e388e45c8825f19f97e878b453ea1 100w,\nhttps://elevator-media.imgix.net/2019/10/26fa0941-1a48-4cc3-ba2f-4f550c868ff6.jpeg?w=116&ixlib=js-2.1.2&s=94bc0315bde9773f73c25b5e55e62fe8 116w,\nhttps://elevator-media.imgix.net/2019/10/26fa0941-1a48-4cc3-ba2f-4f550c868ff6.jpeg?w=134&ixlib=js-2.1.2&s=97a151ff076d56f030172490462d6c48 134w,\nhttps://elevator-media.imgix.net/2019/10/26fa0941-1a48-4cc3-ba2f-4f550c868ff6.jpeg?w=156&ixlib=js-2.1.2&s=b1cdd44aef8234e1b9e8b4ea1b6fc241 156w,\nhttps://elevator-media.imgix.net/2019/10/26fa0941-1a48-4cc3-ba2f-4f550c868ff6.jpeg?w=182&ixlib=js-2.1.2&s=f40a2ba5c5407fac1f310247d18f59b7',
  },
  slug: '',
  type: null,
  publishedAt: '',
  authors: [],
  categories: [],
};
